import { IFormSection } from "../../model/form/FormSection";
import { IHttpService } from "../HttpService";
import FormDetailsMapper from "./mappers/FormDetailsMapper";

export interface IApplicantFormService {
  getApplicantFormDetails(formVersionId: number): Promise<IFormSection[]>;
  markForAdditionalCheck(
    applicantReportingPeriodId: number,
    reasonAdditionalCheck?: string
  ): Promise<any>;
  updateStatusOfAdditionalCheck(
    applicantReportingPeriodId: number,
    statusAdditionalCheck?: string
  ): Promise<any>;
  setFormVersionForAnnouncement(
    formVersionId: number,
    forAnnouncement?: string
  ): Promise<any>;
  updateElectronicRegistryNumber(
    formVersionId: number,
    numberItemElectronicRegistry?: string,
    subNumberItemElectronicRegistry?: string
  ): Promise<any>;
  getAdditionalEvaluationResult(
    reportingPeriodApplicantId: number
  ): Promise<string>;
  updateAdditionalEvaluationResult(
    reportingPeriodApplicantId: number,
    additionalEvaluationResult: string
  ): Promise<void>;
  isSignedReportRequiredForAnnouncement(): Promise<boolean>;
}

const ApplicantFormService = (httpService): IApplicantFormService => {
  const _http: IHttpService = httpService;
  const _basePath: string = "/api";
  const _formsPath = "forms";
  const _signedReportPath = "signed-report";
  const _requiredForAnnouncementPath = "required-for-announcement";
  const _detailsPath = "details";
  const _additionalCheckPath = "additional-check";
  const _statusPath = "status";
  const _announcementPath = "announcement";
  const _electronicRegistryPath = "electronic-registry";
  const _resultPath = "result";

  return {
    async getApplicantFormDetails(formVersionId: number) {
      const path = _http.buildPath(
        _basePath,
        _formsPath,
        formVersionId.toString(),
        _detailsPath
      );
      const response = await _http.get(path);
      const responseJSON = await _http.toJSON(response);
      const {
        formSections: sections,
        formSectionQualifiers: qualifiers,
        qualifierAttributes: attributes,
        formInputValues: values
      } = await responseJSON;

      return FormDetailsMapper(sections, qualifiers, attributes, values);
    },

    async markForAdditionalCheck(
      applicantReportingPeriodId: number,
      reasonAdditionalCheck?: string
    ) {
      const path = _http.buildPath(
        _basePath,
        _formsPath,
        applicantReportingPeriodId.toString(),
        _additionalCheckPath
      );
      const response = await _http.put(path, {
        form: true,
        params: {
          reasonAdditionalCheck
        }
      });
      return _http.toText(response);
    },

    async updateStatusOfAdditionalCheck(
      applicantReportingPeriodId: number,
      statusAdditionalCheck?: string
    ) {
      const path = _http.buildPath(
        _basePath,
        _formsPath,
        applicantReportingPeriodId.toString(),
        _additionalCheckPath,
        _statusPath
      );
      const response = await _http.put(path, {
        form: true,
        params: {
          statusAdditionalCheck
        }
      });
      return _http.toText(response);
    },

    async setFormVersionForAnnouncement(
      formVersionId: number,
      forAnnouncement?: string
    ) {
      const path = _http.buildPath(
        _basePath,
        _formsPath,
        formVersionId.toString(),
        _announcementPath
      );
      const response = await _http.put(path, {
        form: true,
        params: {
          forAnnouncement
        }
      });
      return _http.toText(response);
    },

    async updateElectronicRegistryNumber(
      formVersionId: number,
      numberItemElectronicRegistry?: string,
      subNumberItemElectronicRegistry?: string
    ) {
      const path = _http.buildPath(
        _basePath,
        _formsPath,
        formVersionId.toString(),
        _electronicRegistryPath
      );
      const response = await _http.put(path, {
        form: true,
        params: {
          numberItemElectronicRegistry,
          subNumberItemElectronicRegistry
        }
      });
      return _http.toText(response);
    },

    async getAdditionalEvaluationResult(reportingPeriodApplicantId: number) {
      const path = _http.buildPath(
        _basePath,
        _formsPath,
        reportingPeriodApplicantId.toString(),
        _additionalCheckPath,
        _resultPath
      );
      const response = await _http.get(path);
      return _http.toText(response);
    },

    async updateAdditionalEvaluationResult(
      reportingPeriodApplicantId: number,
      additionalEvaluationResult: string
    ) {
      const path = _http.buildPath(
        _basePath,
        _formsPath,
        reportingPeriodApplicantId.toString(),
        _additionalCheckPath,
        _resultPath
      );
      await _http.put(path, {
        form: true,
        params: {
          additionalEvaluationResult
        }
      });
    },

    async isSignedReportRequiredForAnnouncement() {
      const path = _http.buildPath(
        _basePath,
        _formsPath,
        _signedReportPath,
        _requiredForAnnouncementPath
      );
      const response = await _http.get(path);
      return _http.toJSON(response);
    }
  };
};

export default ApplicantFormService;
